import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogContent,
  IconButton,
  InputAdornment,
  Link,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";

export default function USFSLinkDialog({
  open,
  onClose,
  onSubmit,
  loading = false,
  error = "",
  initialUsername = "",
  defaultAutoSync = true,
  skaterName = "", // If provided, shows skater-specific messaging
}) {
  const [username, setUsername] = useState(initialUsername);
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    if (!open) return;
    setUsername(initialUsername || "");
    setPassword("");
    setShowPassword(false);
  }, [open, initialUsername]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Consent is implied by clicking LOGIN after seeing the terms
    await onSubmit({ username, password, consent: true, autoSync: defaultAutoSync });
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && username && password && !loading) {
      handleSubmit(e);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: "hidden",
        },
      }}
    >
      <Box sx={{ position: "relative" }}>
        <IconButton
          onClick={onClose}
          disabled={loading}
          sx={{
            position: "absolute",
            top: 8,
            right: 8,
            zIndex: 1,
          }}
        >
          <CloseIcon />
        </IconButton>

        <DialogContent sx={{ pt: 4, pb: 3 }}>
          <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 2,
            }}
          >
            {/* USFS Logo */}
            <Box
              component="img"
              src="/USFig-logo_45d2fa64-d493-4159-8815-4314543b5158.webp"
              alt="U.S. Figure Skating"
              sx={{
                height: 60,
                mb: 1,
              }}
            />

            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                color: "primary.main",
                letterSpacing: 2,
                mb: 1,
              }}
            >
              {skaterName ? `Link ${skaterName}'s Account` : "LOGIN"}
            </Typography>

            {skaterName && (
              <Alert severity="warning" sx={{ width: "100%", mb: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>
                  ⚠️ Use {skaterName}'s OWN credentials
                </Typography>
                <Typography variant="body2">
                  Enter the skater's Member Number and password — NOT a parent's account.
                  The browser may try to auto-fill the wrong credentials.
                </Typography>
              </Alert>
            )}

            {error && (
              <Alert severity="error" sx={{ width: "100%" }}>
                {error}
              </Alert>
            )}

            <TextField
              label={skaterName ? `${skaterName}'s Member Number` : "Member Number"}
              placeholder="U.S. Figure Skating or Learn to Skate USA"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              onKeyDown={handleKeyDown}
              fullWidth
              autoComplete="off"
              autoFocus
              disabled={loading}
              sx={{ mb: 1 }}
              name={skaterName ? `usfs-member-${skaterName}` : 'usfs-member'}
              inputProps={{ autoComplete: 'off', 'data-lpignore': 'true', 'data-form-type': 'other' }}
            />

            <TextField
              label={skaterName ? `${skaterName}'s USFS Password` : "USFS Password"}
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyDown={handleKeyDown}
              fullWidth
              autoComplete="new-password"
              disabled={loading}
              name={skaterName ? `usfs-password-${skaterName}` : 'usfs-password'}
              inputProps={{ autoComplete: 'new-password', 'data-lpignore': 'true', 'data-form-type': 'other' }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      size="small"
                    >
                      {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 2 }}
            />

            <Button
              type="submit"
              variant="contained"
              fullWidth
              disabled={!username || !password || loading}
              sx={{
                py: 1.5,
                borderRadius: 1,
                fontWeight: 600,
                fontSize: "1rem",
                textTransform: "none",
              }}
            >
              {loading ? "Logging in..." : "LOGIN"}
            </Button>

            <Typography variant="body2" color="text.secondary" sx={{ mt: 1, textAlign: "center" }}>
              Having trouble? Visit{" "}
              <Link
                href="https://www.usfigureskating.org"
                target="_blank"
                rel="noopener noreferrer"
              >
                usfigureskating.org
              </Link>{" "}
              to reset your password or manage your account.
            </Typography>

            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mt: 2, textAlign: "center" }}
            >
              By authenticating with your USFS account, you agree to our{" "}
              <Link href="#" onClick={(e) => e.preventDefault()}>
                Terms of Service
              </Link>{" "}
              and{" "}
              <Link href="#" onClick={(e) => e.preventDefault()}>
                Privacy Policy
              </Link>
              .
            </Typography>
          </Box>
        </DialogContent>
      </Box>
    </Dialog>
  );
}
